import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import GoogleMapReact from 'google-map-react';

import useLocation from 'apps/web_forms/hooks/useLocation';
import LocationInput from 'apps/web_forms/assets/location-green.svg';
import CloseIcon from 'apps/web_forms/assets/close.svg';
import MapMarker from 'apps/web_forms/components/MapMarker';
import { COLORS, FONT_WEIGHT, GOOGLE_MAPS_API_KEY } from 'apps/web_forms/utils/constants';

const MapContainer = styled.div`
  position: relative;
  height: 45vh;
  width: 100%;

  .empty-map-message {
    display: none;
    position: absolute;
    width: 100%;
    height: 100%;
    flex-direction: column;
    justify-content: center;
    align-items: center;
    font-size: 1.25em;
    font-weight: 300;
    background-color: white;
    z-index: 10;

    .empty-map-description {
      font-size: 0.75em;
    }
  }

  &.empty-map {
    border: 1px solid #ccc;

    .empty-map-message {
      display: flex;
    }
  }
`;

const MapItemsContainer = styled.div`
  width: 100%;

  .position-info {
    background-color: ${COLORS.lightgrey};
    padding: 0.5em 0.25em;
    font-size: 0.75em;
    text-align: center;
    min-height: 2em;
    display: flex;
    justify-content: center;
    align-items: center;
    &.error {
      color: red;
    }
  }

  .set-location {
    width: 100%;
    margin-top: 0.5em;
    margin-bottom: 0.3em;
    display: flex;
    font-weight: ${FONT_WEIGHT.bold};
    font-size: 0.95em;
    align-items: center;
    justify-content: center;
    .location-icon {
      width: 2.5em;
      height: 2.5em;
      fill: ${COLORS.green};
    }
    button {
      line-height: 0;
      border: none;
      background-color: ${(props) => props.locationButtonColor};
      outline: none;
    }
    p {
      margin-left: 0.5em;
    }
  }
`;

const RemoveLocationButton = styled.button`
  background-color: ${COLORS.lightgrey};
  width: 1.5em;
  height: 1.5em;
  position: absolute;
  top: 0.5em;
  left: 0.5em;
  z-index: 1;
  border-radius: 50%;
  border: none;
  svg {
    width: 0.75em;
    height: 0.75em;
    fill: white;
    margin-top: 0.2em;
  }
`;

const Map = ({
  zoom,
  allowRemoveMap,
  children: markerListItem,
  markers,
  canOverride,
  removeLocationHandler,
  getLocationHandler,
  defaultLocation,
  defaultMessage,
  openOnLoad,
  onDragMethod,
  locationButtonColor,
  centerOnMarkers,
}) => {
  const {
    center,
    locationStrings,
    locationError,
    positionInfo,
    changePosition,
    setUserAccuracy,
    clearLocation,
  } = useLocation();

  const [coords, setCoords] = useState();
  const [positionMessage, setPositionMessage] = useState();

  const onDrag = ({ center: { lat, lng } }) =>
    canOverride && changePosition({ lat: lat(), lng: lng() });

  useEffect(() => {
    if (openOnLoad) {
      setUserAccuracy();
    }
  }, [openOnLoad]);

  useEffect(() => {
    if (center && locationStrings) {
      getLocationHandler(center, locationStrings);
    }
  }, [center, locationStrings]);

  useEffect(() => {
    setCoords(defaultLocation);
    setPositionMessage(defaultMessage);
  }, [defaultLocation, defaultMessage]);

  useEffect(() => {
    if (center) setCoords(center);
    if (positionInfo) setPositionMessage(positionInfo);
  }, [center, positionInfo]);

  const getMapBounds = (map, maps, places) => {
    const bounds = new maps.LatLngBounds();
    places.forEach((place) => {
      bounds.extend(new maps.LatLng(place.latitude, place.longitude));
    });
    bounds.extend(new maps.LatLng(coords.lat, coords.lng));
    return bounds;
  };

  const apiIsLoaded = (map, maps, places) => {
    if (centerOnMarkers) {
      const bounds = getMapBounds(map, maps, places);
      map.fitBounds(bounds);
    }
  };

  return (
    <>
      <MapContainer
        className={` 
        ${!coords ? 'empty-map' : ''}
      `}
      >
        <div className="empty-map-message" data-testid="empty-map">
          <p>Empty location</p>
          <p className="empty-map-description">
            Press the button below to set your location
          </p>
        </div>

        {coords && allowRemoveMap && (
          <RemoveLocationButton
            type="button"
            onClick={() => {
              removeLocationHandler();
              clearLocation();
            }}
            data-testid="remove-map-button"
          >
            <CloseIcon />
          </RemoveLocationButton>
        )}

        {coords && (
          <GoogleMapReact
            bootstrapURLKeys={{
              key: GOOGLE_MAPS_API_KEY,
            }}
            zoom={zoom}
            center={coords}
            draggable={!!canOverride}
            onDragEnd={onDragMethod || onDrag}
            hoverDistance={60}
            yesIWantToUseGoogleMapApiInternals
            onGoogleApiLoaded={({ map, maps }) => {
              apiIsLoaded(map, maps, markers);
            }}
          >
            <MapMarker lat={coords.lat} lng={coords.lng} text="My Marker" />
            {markers.map(markerListItem)}
          </GoogleMapReact>
        )}
      </MapContainer>

      <MapItemsContainer locationButtonColor={locationButtonColor}>
        {positionMessage && (
          <div className={`position-info ${locationError && 'error'}`}>
            {positionMessage}
          </div>
        )}

        <div className="set-location">
          <button
            type="button"
            onClick={setUserAccuracy}
            data-testid="set-location-input"
          >
            <LocationInput className="location-icon" />
          </button>
          <p>Set Current Location</p>
        </div>
      </MapItemsContainer>
    </>
  );
};

Map.propTypes = {
  zoom: PropTypes.number,
  allowRemoveMap: PropTypes.bool,
  children: PropTypes.func,
  markers: PropTypes.arrayOf(PropTypes.shape({})),
  canOverride: PropTypes.bool,
  removeLocationHandler: PropTypes.func,
  getLocationHandler: PropTypes.func,
  defaultLocation: PropTypes.shape({
    lat: PropTypes.number,
    lng: PropTypes.number,
  }),
  defaultMessage: PropTypes.string,
  openOnLoad: PropTypes.bool,
  centerOnMarkers: PropTypes.bool,
  onDragMethod: PropTypes.func,
  locationButtonColor: PropTypes.string,
};

Map.defaultProps = {
  zoom: 14.27,
  allowRemoveMap: true,
  children: () => {},
  markers: [],
  canOverride: false,
  removeLocationHandler: () => {},
  getLocationHandler: () => {},
  defaultLocation: null,
  defaultMessage: null,
  openOnLoad: false,
  centerOnMarkers: false,
  onDragMethod: null,
  locationButtonColor: 'none',
};

export default Map;
